import {
  Box,
  Grid,
  Paper,
  RadioGroup,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  FormControl,
  Radio,
  FormLabel,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useCustomSnackBar } from '../../shared/SnackBar/useCustomSnackBar';
import styled from 'styled-components';
import { StyledPaper } from '../../shared/Styled/Styled';
import { LoadingButton } from '@mui/lab';
import { lazy, object, SchemaOf, string } from 'yup';
import { Save, Delete, ExpandMore } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormSwitch } from '../../shared/FormSwitch/FormSwitch';
import { CustomSelect, SelectOption } from '../../shared/CustomSelect/CustomSelect';
import { CustomTextFieldInput } from '../../shared/CustomTextFieldInput/CustomTextFieldInput';
import { AutocompleteOff, DateTimePickerFormat } from '../CampaignsPage/shared';
import { ProductPriceCard } from './components/ProductPriceCard';
import { calculateProductDefaultPrices } from '../../apis/pricing-api';
import placeholder from '../../assets/img/placeholder-sq.jpeg';
import { FormRadio } from '../../shared/FormRadio/FormRadio';
import { IngredientQuantityChangeType, ProductRecipeGrid } from './components/ProductRecipeGrid';
import { useS4DComparison } from './hooks/useS4DComparison';
import { ProductIngredientSearch } from './components/ProductIngredientSearch';
import { postToSlack } from '../../apis/utils';
import Bottleneck from 'bottleneck';
import {
  addProductToDefaultMenu,
  allProductMenusCacheKey,
  allProductPricesCacheKey,
  allProductsCacheKey,
  defaultMenuCacheKey,
  getProductPrices,
  isProductInDefaultMenu,
  removeProductFromDefaultMenu,
  singleProductCacheKey,
  upcomingProductPricesCacheKey,
  upcomingProductsCacheKey,
  updateProductPrice,
  upsertProduct,
  upsertProductPriceGroups,
  useProduct,
  useProductCategorySizes,
  useProductPriceGroups,
  useProductSizes,
} from '../../apis/products-api';
import {
  DisplayGroupDto,
  IngredientDto,
  ProductCategoryDetailsDto,
  ProductImageDto,
  ProductIngredientDto,
  ProductPriceDto,
  ProductPriceGroupEnum,
  ProductPriceModel,
  ProductSizeDto,
  TagDto,
  UpsertProductPriceGroupDto,
} from '@kotipizzagroup/kotipizza-products-api-client';
import { ProductSizeCard } from './components/ProductSizeCard';
import { ProductPriceCalculationRequest } from '@kotipizzagroup/kotipizza-pricing-api-client';
import { Commerce } from '@kotipizzagroup/kotipizza-shared-types';
import LoadingOverlay from '../../shared/LoadingOverlay/LoadingOverlay';
import { MuiColorInput, matchIsValidColor } from 'mui-color-input';
import { cloneDeep } from 'lodash';
import { SearchInput } from '../../shared/Search/SearchInput';
import ProductLinks from './components/ProductLinks';
import {
  backgroundColorProductCategories,
  ProductDetailsViewParams,
  ProductFormData,
  ProductImagesFormData,
  ProductSizeModel,
} from './types';
import { DateTimePicker } from '../../shared/DateTimePicker/DateTimePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormCheckbox } from '../../shared/FormCheckbox/FormCheckbox';
import { TimePicker } from '../../shared/TimePicker/TimePicker';
import { TagSelect } from '../../shared/TagSelect/TagSelect';
import { ovenProductCategoryIds } from '../../shared/utils/productAndIngredientUtils';
import { useProductData } from './hooks/useProductData';
import { generateChangeLog } from '../../shared/utils/formatters';
import { getProductsApiError } from '../../apis/products-api.errors';

dayjs.extend(utc);

const limiter = new Bottleneck({
  maxConcurrent: 1,
});

const productImagesWithValues = (object: ProductImagesFormData): ProductImagesFormData => {
  const objectsWithValues = cloneDeep(object);
  for (const key in objectsWithValues) {
    const productImage = objectsWithValues[key] as ProductImageDto;
    if (
      !productImage.imageUrl &&
      !productImage.mobileImageUrl &&
      !productImage.menuImageUrl &&
      !productImage.mobileMenuImageUrl
    ) {
      delete objectsWithValues[key]; // Remove empty product image objects, keep only the ones that contains one or more value
    }
  }
  return objectsWithValues;
};

const mapRules = <T,>(map: ProductImagesFormData, rule: T): Record<string, T> => {
  const objectsWithValues = productImagesWithValues(map);
  return Object.keys(objectsWithValues).reduce((newMap, key) => ({ ...newMap, [key]: rule }), {});
};

const dynamicObjectValue: SchemaOf<{
  imageUrl: string;
  mobileImageUrl: string;
  menuImageUrl: string;
  mobileMenuImageUrl: string;
}> = object({
  imageUrl: string().required('Tuotesivu desktop -kuva on pakollinen'),
  mobileImageUrl: string().required('Tuotesivu mobile -kuva on pakollinen'),
  menuImageUrl: string().required('Menusivu desktop -kuva on pakollinen'),
  mobileMenuImageUrl: string().required('Menu-sivun mobile -kuva pakollinen'),
});

const productImagesFormDataSchema = lazy((map) => object(mapRules<typeof dynamicObjectValue>(map, dynamicObjectValue)));
const formSchema: SchemaOf<ProductFormData> = object()
  .shape({
    name: string().required(),
    description: string().nullable(),
    productImagesFormData: productImagesFormDataSchema,
  })
  .defined();

const noProductSizes: ProductSizeDto[] = [];

export const ProductDetails: React.FC = () => {
  const [productSizesWithPrice, setProductSizesWithPrice] = useState<ProductSizeModel[]>([]);
  const [imgPath, setImagePath] = useState(placeholder);
  const [productIngredientsRows, setProductIngredientsRows] = useState<ProductIngredientDto[]>([]);
  const [loadedRecipeProductId, setLoadedRecipeProductId] = useState<number>();
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);
  const [inputPrice, setInputPrice] = useState<Record<number, number>>();
  const [selectedSizeIds, setSelectedSizeIds] = useState<number[]>([]);
  const [isInDefaultMenu, setIsInDefaultMenu] = useState(true);
  const [isInConciseMenu, setIsInConciseMenu] = useState(true);
  const [isScheduled, _setIsScheduled] = useState(false);
  const [backgroundColor, _setBackgroundColor] = useState('');
  const [selectedProductSizeId, setSelectedProductSizeId] = useState<number>();
  const [productIngredientRecommendations, setProductIngredientRecommendations] = useState<IngredientDto[]>([]);
  const [productTags, setProductTags] = useState<TagDto[]>([]);
  const [productDisplayGroups, setProductDisplayGroups] = useState<DisplayGroupDto[]>([]);
  const [productPriceGroups, setProductPriceGroups] = useState<UpsertProductPriceGroupDto[]>([]);

  const setBackgroundColor = (value: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue(`productImagesFormData.${selectedProductSizeId}.backgroundColor` as any, value);
    _setBackgroundColor(value);
  };

  const setIsScheduled = (value: boolean) => {
    _setIsScheduled(value);
    if (value) {
      setValue('active', true);
    } else {
      setValue('startDate', undefined);
      setValue('endDate', undefined);
    }
  };

  const { id } = useParams<ProductDetailsViewParams>();
  const isNewProduct = isNaN(Number(id));
  const { t } = useTranslation();
  const snackBar = useCustomSnackBar();
  const upsertProductMutation = useMutation(upsertProduct);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productId = isNewProduct ? 0 : Number(id);

  const {
    data: product,
    isInitialLoading: isInitialLoadingProduct,
    isFetching: isFetchingProduct,
  } = useProduct(productId, { enabled: !isNewProduct });

  const [hasLimitedSaleTime, _setLimitedSaleTime] = useState(
    Boolean(product?.availableForSaleFrom) || Boolean(product?.availableForSaleUntil)
  );

  const setLimitedSaleTime = (value: boolean) => {
    _setLimitedSaleTime(value);
    if (!value) {
      setValue('availableForSaleFrom', undefined);
      setValue('availableForSaleUntil', undefined);
    }
  };

  const {
    products,
    productCategories,
    displayGroups,
    allIngredients,
    defaultMenu,
    conciseMenu,
    allVatRates,
    tags,
    isInitialLoading: isInitialLoadingProductData,
    isFetching: isFetchingProductData,
  } = useProductData();

  const {
    data: initialProductPriceGroups,
    isInitialLoading: isInitialLoadingProductPriceGroups,
    isFetching: isFetchingProductPriceGroups,
  } = useProductPriceGroups(productId, !isNewProduct);

  const handlePriceChange = (price: number, productSizeId: number) => {
    setInputPrice({ ...inputPrice, [productSizeId]: price });
  };

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProductFormData>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      priceModel: ProductPriceModel.FIXED_MIN_PRICE,
      active: false,
      isAgeRestricted: false,
      isHiddenFromMenu: false,
      isArchived: false,
      isNonDiscountable: false,
      isSpicy: false,
      productImagesFormData: {},
      startDate: undefined,
      endDate: undefined,
      vatId: 1,
    },
  });

  const categoryId = watch('productCategoryId') || 0;
  const productUrl = watch('url') || '';
  const isActive = watch('active');
  const productImages = watch('productImagesFormData');

  const {
    data: categorySizes,
    isInitialLoading: isInitialLoadingCategorySizes,
    isFetching: isFetchingCategorySizes,
  } = useProductCategorySizes(categoryId);

  const {
    data: productSizes,
    isInitialLoading: isInitialLoadingProductSizes,
    isFetching: isFetchingProductSizes,
  } = useProductSizes(productId, {
    enabled: !isNewProduct,
  });

  // One comparison for the whole page: the recipe grid and the S4D links below read the same
  // differences the product list shows.
  const s4dComparison = useS4DComparison({
    product,
    recipe: productIngredientsRows,
    productSizes: productSizes || noProductSizes,
    sizePrices: productSizesWithPrice,
  });
  const isRecipeLoaded = isNewProduct || loadedRecipeProductId === productId;

  const handleSizeToggle = (sizeId: number, checked: boolean) => {
    if (!checked) {
      setSelectedSizeIds(selectedSizeIds.filter((s) => s !== sizeId));
    } else {
      setSelectedSizeIds([...selectedSizeIds, sizeId]);
    }
  };

  const hasSize = selectedSizeIds.length > 0;

  const isInitialLoadComplete =
    !isInitialLoadingProductData &&
    !isInitialLoadingProduct &&
    !isInitialLoadingCategorySizes &&
    !isInitialLoadingProductSizes &&
    !isInitialLoadingProductPriceGroups;

  const isFetching =
    isFetchingProductData ||
    isFetchingProduct ||
    isFetchingCategorySizes ||
    isFetchingProductSizes ||
    isFetchingPrices ||
    isFetchingProductPriceGroups;

  const isSaveDisabled =
    !isInitialLoadComplete ||
    isFetching ||
    categoryId === 0 ||
    (isActive && !hasSize) ||
    upsertProductMutation.isLoading ||
    product?.isArchived;

  const showProductRecommendation = useMemo(
    () => products && product && product.productCategoryId === Commerce.ProductCategory.PIZZA,
    [product, products]
  );

  const showAlternativeProductSuggestion = useMemo(
    () => products && product && ovenProductCategoryIds.includes(product.productCategoryId),
    [product, products]
  );

  const alternativeProductSuggestionValues = useMemo(() => {
    if (!products) return [];

    return products
      .filter((p) => ovenProductCategoryIds.includes(p.productCategoryId))
      .filter((p) => p.isArchived === false)
      .map((p) => ({ label: p.name, value: p.productId?.toString() || '' }))
      .sort(sortByLabel);
  }, [products]);

  const alternativeProductSuggestionId = watch('alternativeProductSuggestion.productId');
  const selectedAlternativeProductSuggestion = useMemo(() => {
    if (!products || !alternativeProductSuggestionId) return '';
    return products.find((p) => p.productId === alternativeProductSuggestionId)?.name || '';
  }, [products, alternativeProductSuggestionId]);

  const productRecommendationValues = useMemo(() => {
    if (!products) return [];

    return products
      .filter((p) => p.productCategoryId === Commerce.ProductCategory.DIP)
      .filter((p) => p.isArchived === false)
      .map((p) => ({ label: p.name, value: p.productId?.toString() || '' }))
      .sort(sortByLabel);
  }, [products]);

  const chefsRecommendationIngredientOptions = useMemo(() => {
    if (!allIngredients) return [];

    return allIngredients
      .filter(
        (i) =>
          i.ingredientCategoryId === Commerce.IngredientCategory.INGREDIENT ||
          i.ingredientCategoryId === Commerce.IngredientCategory.SPICE
      )
      .filter((i) => i.isArchived === false)
      .map((ingredient) => ({
        label: ingredient.name,
        value: String(ingredient.ingredientId),
      }))
      .sort(sortByLabel);
  }, [allIngredients]);

  const recommendedProductId = watch('productRecommendation.recommendedProductId');
  const selectedProductRecommendation = useMemo(() => {
    if (!products || !recommendedProductId) return '';
    return products.find((p) => p.productId === recommendedProductId)?.name || '';
  }, [products, recommendedProductId]);

  const vatRatesOptions = useMemo((): SelectOption[] => {
    return (
      allVatRates?.map((vat) => ({
        title: `${vat.vatRate.toLocaleString('fi-FI')} %`,
        value: vat.id ?? 0,
      })) ?? []
    );
  }, [allVatRates]);

  const handlePriceGroupPriceChange = (price: number, sizeId: number, group: ProductPriceGroupEnum) => {
    setProductPriceGroups((prev) => {
      const existingIndex = prev.findIndex((i) => i.productSizeId === sizeId && i.productPriceGroup === group);
      if (existingIndex >= 0) {
        prev[existingIndex].price = price;
      } else {
        prev.push({
          productSizeId: sizeId,
          productPriceGroup: group,
          price: price,
        });
      }
      return prev;
    });
  };

  /**
   * EFFECTS
   */

  // Select first of active product sizes for default value for image if selectedProductSizeId is not set
  useEffect(() => {
    if (selectedProductSizeId === undefined && selectedSizeIds[0]) {
      setSelectedProductSizeId(selectedSizeIds[0]);
    }
  }, [selectedProductSizeId, selectedSizeIds]);

  // Set price group values from initial load.
  useEffect(() => {
    if (isInitialLoadComplete && initialProductPriceGroups && productSizes) {
      setProductPriceGroups(initialProductPriceGroups);
    }
  }, [initialProductPriceGroups, isInitialLoadComplete, productSizes]);

  // Set background color and image when product size is changed or set for the first time
  useEffect(() => {
    if (selectedProductSizeId) {
      _setBackgroundColor(productImages?.[selectedProductSizeId]?.backgroundColor || '');
      setImagePath(productImages?.[selectedProductSizeId]?.imageUrl || placeholder);
    }
  }, [productImages, selectedProductSizeId]);

  // Check if product is in default menu
  useEffect(() => {
    if (!defaultMenu?.productIds || isNewProduct) return;

    setIsInDefaultMenu(defaultMenu.productIds.some((pId) => Number(id) === pId));
  }, [defaultMenu, id, isNewProduct]);

  // Check if product is in concise menu
  useEffect(() => {
    if (!conciseMenu?.productIds || isNewProduct) return;

    setIsInConciseMenu(conciseMenu.productIds.some((pId) => Number(id) === pId));
  }, [conciseMenu, id, isNewProduct]);

  useEffect(() => {
    if (!isInDefaultMenu) {
      setIsInConciseMenu(false);
    }
  }, [isInDefaultMenu]);

  // Update product category name based on category id.
  useEffect(() => {
    if (productCategories && productCategories.length > 0) {
      const category = productCategories.find((c) => c.productCategoryId === categoryId);
      if (category) {
        setValue('productCategoryName', String(category.productCategoryName));
      }
    }
  }, [categoryId, productCategories, setValue]);

  // Fill in initial product sizes.
  useEffect(() => {
    if (!isFetchingProductSizes && productSizes && isInitialLoadComplete) {
      setSelectedSizeIds(productSizes.map((s) => s.productSizeId));
    }
  }, [isFetchingProductSizes, isInitialLoadComplete, productSizes]);

  // Fill in initial data from product.
  useEffect(() => {
    if (isFetchingProduct || !product || !isInitialLoadComplete || !allIngredients) return;

    setProductIngredientsRows(product.ingredients || []);
    setLoadedRecipeProductId(productId);
    setProductTags(product.tags || []);
    setProductDisplayGroups(product.displayGroups || []);
    setProductIngredientRecommendations(
      allIngredients.filter(
        (i) => i.ingredientId && (product.productIngredientRecommendations || []).includes(i.ingredientId)
      )
    );

    const productImagesFormData: ProductImagesFormData = {};

    product.productImages?.forEach((image) => {
      if (image.productSizeId) {
        productImagesFormData[image.productSizeId] = image;
      }
    });

    let startDate = undefined;
    let endDate = undefined;

    if (product.startDate) {
      startDate = dayjs(product.startDate).local().format(DateTimePickerFormat);
    }

    if (product.endDate) {
      endDate = dayjs(product.endDate).local().format(DateTimePickerFormat);
    }

    _setIsScheduled(Boolean(product.startDate || product.endDate));
    _setLimitedSaleTime(Boolean(product.availableForSaleFrom || product.availableForSaleUntil));

    const vatId = product.vat?.id || 1;

    // Remove possible perfetta pricing model.
    if (product.priceModel === ProductPriceModel.PERFETTA) {
      product.priceModel = ProductPriceModel.FIXED_MIN_PRICE;
    }

    reset({ ...product, productImagesFormData, startDate, endDate, vatId });
  }, [isFetchingProduct, isInitialLoadComplete, product, productId, reset, allIngredients]);

  // Remove selected size ids if category is changed.
  useEffect(() => {
    const categorySizeIds = categorySizes?.map((cs) => cs.productSizeId);
    const previouslySelected = selectedSizeIds.filter((id) => !categorySizeIds?.includes(id));
    if (previouslySelected.length > 0) {
      setSelectedSizeIds([]);
    }
  }, [categorySizes, selectedSizeIds]);

  useEffect(() => {
    const subscription = watch((value) => {
      if (selectedProductSizeId) {
        setImagePath(value.productImagesFormData?.[selectedProductSizeId]?.imageUrl || placeholder);
      }
    });
    return () => subscription.unsubscribe();
  }, [selectedProductSizeId, watch]);

  useEffect(() => {
    if (product) {
      const category = productCategories?.find((x) => x.productCategoryId == product.productCategoryId);
      if (category) {
        setProductSizesWithPrice(
          category.productSizes?.map((ps) => {
            return {
              sizeId: ps.productSizeId || 0,
              sizeName: ps.name || '',
              calculatedPrice: 0,
              minimumPrice: 0,
              price: product.price || null,
            };
          }) || []
        );
      }
    }
  }, [product, productCategories, reset, t]);

  useEffect(() => {
    if (categoryId !== Commerce.ProductCategory.ALCOHOL || !isNewProduct) {
      return;
    }

    // Prefill default values for new alcohol products
    reset({
      ...getValues(),
      availableForSaleFrom: '09:00',
      availableForSaleUntil: '21:00',
      vatId: 2,
      isNonDiscountable: true,
    });

    _setLimitedSaleTime(true);
  }, [categoryId, isNewProduct, getValues, reset]);

  const setCalculatedSizePrice = useCallback(
    (data: Pick<ProductSizeModel, 'sizeId' | 'calculatedPrice' | 'minimumPrice' | 'price' | 'sizeName'>) => {
      setProductSizesWithPrice((ps) => {
        const sizeIds = ps.map((m) => m.sizeId);
        // In case of creating new product we might need to push the value instead of just updating prices.
        if (isNewProduct && !sizeIds.includes(data.sizeId)) {
          ps.push({
            calculatedPrice: data.calculatedPrice,
            minimumPrice: data.minimumPrice,
            price: data.price,
            sizeId: data.sizeId,
            sizeName: data.sizeName,
          });
        }
        return ps.map((item) => {
          if (item.sizeId === data.sizeId) {
            return {
              ...item,
              calculatedPrice: data.calculatedPrice,
              minimumPrice: data.minimumPrice,
              price: data.price,
            };
          }
          return item;
        });
      });
    },
    [isNewProduct]
  );

  useEffect(() => {
    const fetchPrices = async () => {
      const { hasFixedMinimumPrice, hasMinimumPrice } = getValues();

      if (isInitialLoadComplete && selectedSizeIds.length > 0 && categorySizes) {
        setIsFetchingPrices(true);
        try {
          // In case of creating new product we can use bare minimum DTO data for price calculation.
          const productData = product || { hasFixedMinimumPrice, hasMinimumPrice };
          // Get current prices in case we have existing product.
          const prices = productId ? await getProductPrices(productId) : [];

          const enabledSizes = categorySizes.filter((s) => selectedSizeIds.includes(s.productSizeId));

          const defaultPrices = await calculateProductDefaultPrices({
            product: { ...productData, ingredients: productIngredientsRows },
            ingredients: allIngredients || [],
            productSizes: enabledSizes,
          } as ProductPriceCalculationRequest);

          enabledSizes.forEach((enabledSize) => {
            const sizeId = enabledSize.productSizeId;
            const sizeName = enabledSize.name;
            if (!sizeId || !sizeName) return;
            const sizeDefaultPrice = defaultPrices.find((p) => p.productSizeID === sizeId);
            const sizePrice = prices.find((p) => p.productSizeId === sizeId);

            setCalculatedSizePrice({
              sizeId,
              sizeName,
              calculatedPrice: sizeDefaultPrice?.calculatedPrice || 0,
              minimumPrice: sizePrice?.minimumPrice || 0,
              price: sizePrice?.price || 0,
            });
          });
        } catch (e) {
          console.error('Failed to fetch product prices', e);
          snackBar.showError(getProductsApiError(e).message);
        } finally {
          setIsFetchingPrices(false);
        }
      }
    };

    limiter.schedule(() => {
      return fetchPrices();
    });
  }, [
    allIngredients,
    categorySizes,
    getValues,
    isInitialLoadComplete,
    isNewProduct,
    product,
    productId,
    productIngredientsRows,
    selectedSizeIds,
    setCalculatedSizePrice,
    snackBar,
  ]);

  const handleIngredientQuantityChange = (ingredientId: number, changeType: IngredientQuantityChangeType) => {
    setProductIngredientsRows((currentRows) =>
      currentRows
        ?.map((row) => {
          if (row.ingredientId === ingredientId) {
            return {
              ...row,
              quantity:
                changeType === IngredientQuantityChangeType.ADD
                  ? (row.quantity as number) + 1
                  : (row.quantity as number) - 1,
            };
          }
          return { ...row };
        })
        .filter((x) => x.quantity)
    );
  };

  const newProductIngredient = (productId: number, ingredientId: number): ProductIngredientDto => {
    return {
      ingredientId,
      maxQuantity: 3,
      minQuantity: 0,
      productId: productId,
      quantity: 1,
    };
  };

  const handleAddIngredient = (ingredientId: number) => {
    if (productIngredientsRows?.find((x) => x.ingredientId === ingredientId)) {
      snackBar.showError('Ingredient already part of the product recipe');
      return;
    }

    const productId = product?.productId || 0; // 0 is used for new products
    const newIngredient = newProductIngredient(productId, ingredientId);
    setProductIngredientsRows((currentRows) => currentRows && [...currentRows, newIngredient]);
  };

  const handleMaxQuantityChange = (ingredientId: number, value: string) => {
    setProductIngredientsRows((currentRows) => {
      return currentRows?.map((row) => {
        if (row.ingredientId === ingredientId) {
          return { ...row, maxQuantity: +value };
        }
        return row;
      });
    });
  };

  const handleIngredientRequiredChange = (ingredientId: number, value: boolean) => {
    setProductIngredientsRows((currentRows) => {
      return currentRows?.map((row) => {
        if (row.ingredientId === ingredientId) {
          return { ...row, minQuantity: Number(value) };
        }
        return row;
      });
    });
  };

  const getSavedProductIngredients = (savedProduct: ProductFormData, ingredients: ProductIngredientDto[]) => {
    if (!ingredients) return [];

    if (savedProduct.productCategoryId !== Commerce.ProductCategory.ETUPIZZA || savedProduct.productId) {
      return ingredients;
    }

    // Add min/max configuration for Etupizza ingredients
    return ingredients.map((ingredient) => ({
      ...ingredient,
      maxQuantity: ingredient.quantity,
      minQuantity: ingredient.quantity,
    }));
  };

  const getProductImages = (productImagesFormData: ProductImagesFormData): ProductImageDto[] => {
    const productImages = productImagesWithValues(productImagesFormData);
    return Object.entries(productImages).map(([key, value]) => {
      return { ...value, productSizeId: Number(key) };
    });
  };

  const handleProductSave: SubmitHandler<ProductFormData> = async (formData) => {
    try {
      const payload: ProductFormData = {
        ...formData,
        ingredients: [...getSavedProductIngredients(formData, productIngredientsRows)],
        productImagesFormData: {}, //no need to send this
        tags: productTags,
        displayGroups: productDisplayGroups,
        productIngredientRecommendations: productIngredientRecommendations.map((i) => Number(i.ingredientId)),
        productImages: [...getProductImages(formData.productImagesFormData)],
        availableForSaleFrom: formData.availableForSaleFrom ? formData.availableForSaleFrom.slice(0, 5) : null,
        availableForSaleUntil: formData.availableForSaleUntil ? formData.availableForSaleUntil.slice(0, 5) : null,
        // Make sure not to send empty recommendation.
        productRecommendation:
          formData.productRecommendation?.recommendationText && formData.productRecommendation.recommendedProductId
            ? formData.productRecommendation
            : null,
        // Make sure not to send empty alternative suggestion.
        alternativeProductSuggestion:
          formData.alternativeProductSuggestion?.linkText && formData.alternativeProductSuggestion.productId
            ? formData.alternativeProductSuggestion
            : null,
      };

      payload.startDate = payload.startDate ? dayjs(payload.startDate).utc().format() : null;
      payload.endDate = payload.endDate ? dayjs(payload.endDate).utc().format() : null;

      // Set pricing model.
      if (payload.priceModel === ProductPriceModel.FIXED_MIN_PRICE) {
        payload.hasMinimumPrice = true;
        payload.hasFixedMinimumPrice = true;
      }
      if (payload.priceModel === ProductPriceModel.MONSTER) {
        payload.hasMinimumPrice = true;
        payload.hasFixedMinimumPrice = false;
      }

      if (isNewProduct) {
        // slug is generated from product name in Products API but required
        // field so let's send an empty string in case creating new product.
        payload.slug = '';
      }

      const productVat = allVatRates?.find((vat) => vat.id === payload.vatId);
      payload.vat = productVat;

      // Save product
      const response = await upsertProductMutation.mutateAsync(payload);

      const enabledPrices = productSizesWithPrice.filter((item) => selectedSizeIds.includes(item.sizeId));

      if (response.id) {
        const productId = response.id;

        // Save product prices
        const prices: ProductPriceDto[] = enabledPrices.map((item) => {
          // Initial price is either the previously saved mininum price or price calculated for the product size.
          const initialPrice = item.minimumPrice || item.calculatedPrice;

          // Fixed price is something that the user has typed into the price inputs or initial price if fields are untouched.
          const fixedPrice = inputPrice && inputPrice[item.sizeId] ? inputPrice[item.sizeId] : initialPrice;

          const hasEditablePrice =
            payload.priceModel === ProductPriceModel.FIXED_MIN_PRICE ||
            payload.priceModel === ProductPriceModel.MONSTER;

          // This is the value that we save as product sizes minimum price.
          const minimumPrice = hasEditablePrice ? fixedPrice : item.calculatedPrice;

          return {
            productId,
            productSizeId: item.sizeId,
            priceCorrection: 0,
            minimumPrice,
            price: null, // this is not saved anywhere so we can pass null.
          };
        });

        await updateProductPrice(productId, prices);

        // Update product price groups
        await upsertProductPriceGroups(productId, productPriceGroups);

        // Handle product menus for product
        const isIncludedInDefaultMenu = await isProductInDefaultMenu(response.id);

        if (isInDefaultMenu && !isIncludedInDefaultMenu) {
          addProductToDefaultMenu(response.id);
        } else if (!isInDefaultMenu && isIncludedInDefaultMenu) {
          removeProductFromDefaultMenu(response.id);
        }

        const isIncludedInConciseMenu = await isProductInDefaultMenu(response.id, true);

        if (isInConciseMenu && !isIncludedInConciseMenu) {
          addProductToDefaultMenu(response.id, true);
        } else if (!isInConciseMenu && isIncludedInConciseMenu) {
          removeProductFromDefaultMenu(response.id, true);
        }
      }

      await queryClient.invalidateQueries(allProductsCacheKey);
      await queryClient.invalidateQueries(['product-sizes', payload.productId]);
      await queryClient.invalidateQueries(['product-prices', payload.productId]);
      await queryClient.invalidateQueries(['product-category-sizes', payload.productCategoryId]);
      await queryClient.invalidateQueries([defaultMenuCacheKey]);
      await queryClient.invalidateQueries([allProductMenusCacheKey]);
      await queryClient.invalidateQueries(allProductPricesCacheKey);
      await queryClient.invalidateQueries(upcomingProductsCacheKey);
      await queryClient.invalidateQueries(upcomingProductPricesCacheKey);

      if (!payload.productId) {
        snackBar.showSuccess(t('New product created'));
        postToSlack(`${payload.name} created in ${payload.productCategoryName} category.`, 'product-updates');
        navigate(`/products/${response.id}`);
      } else {
        snackBar.showSuccess(t('Product updated'));

        if (product) {
          const changeLogText = generateChangeLog(payload, product, ['productImagesFormData', 'vatId']);
          postToSlack(`${payload.name} edited from Juuri.${changeLogText}`, 'product-updates');
        }

        await queryClient.invalidateQueries(singleProductCacheKey(payload.productId));
      }
    } catch (e) {
      const apiError = getProductsApiError(e);
      snackBar.showError(apiError.message);
    }
  };

  const SaveButton = () => {
    return (
      <LoadingButton
        onClick={handleSubmit(handleProductSave)}
        startIcon={<Save />}
        loading={upsertProductMutation.isLoading}
        disabled={isSaveDisabled}
        variant="contained"
        color="primary"
      >
        {product?.isArchived ? t('products.archivedNoEditing') : t('global.save')}
      </LoadingButton>
    );
  };

  const pageModeTitle = useMemo(() => {
    if (product?.isArchived) return t('products.archivedNoEditing');
    return isNewProduct ? t('product.addProduct') : t('product.editProduct');
  }, [isNewProduct, product?.isArchived, t]);

  return (
    <Box>
      {!isInitialLoadComplete ? <LoadingOverlay /> : null}
      <TopBar>
        <Typography variant="h1">
          {t('product.title')} / {pageModeTitle}
        </Typography>
        <SaveButton />
      </TopBar>
      <Box display="flex">
        <Grid width="100%" display="flex" container spacing={3} justifyContent="flex-start">
          <FormSection>
            <Grid item xs={12}>
              <CustomSelect
                name="productCategoryId"
                control={control}
                label={t('productDetails.productCategoryLabel')}
                emptyText="Valitse tuoteryhmä"
                showEmptyOption={false}
                isLoading={isInitialLoadingProductData}
                isDisabled={product?.isArchived}
                options={
                  productCategories?.map((cat: ProductCategoryDetailsDto) => ({
                    title: cat.productCategoryName,
                    value: cat.productCategoryId,
                  })) || []
                }
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <FormSwitch
                name="active"
                control={control}
                label={t('productDetails.active')}
                uncheckedLabel={t('productDetails.inactive')}
                disabled={product?.isArchived}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <FormControlLabel
                control={<Switch checked={isInDefaultMenu} onChange={(e) => setIsInDefaultMenu(e.target.checked)} />}
                label={t('productDetails.isInDefaultMenu')}
                disabled={product?.isArchived}
              />
            </Grid>
            {isInDefaultMenu && (
              <Grid item xs={12} md={4} lg={3}>
                <FormControlLabel
                  control={<Switch checked={isInConciseMenu} onChange={(e) => setIsInConciseMenu(e.target.checked)} />}
                  label={t('productDetails.isInConciseMenu')}
                  disabled={!isInDefaultMenu}
                />
              </Grid>
            )}
            <Grid item xs={12} md={4} lg={3}>
              <FormSwitch
                name="isHiddenFromMenu"
                control={control}
                label={t('productDetails.isHiddenFromMenuLabel')}
                uncheckedLabel={t('productDetails.isHiddenFromMenuLabel')}
                disabled={product?.isArchived}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <FormSwitch
                name="isAgeRestricted"
                control={control}
                label={t('productDetails.isAgeRestrictedLabel')}
                uncheckedLabel={t('productDetails.isAgeRestrictedLabel')}
                disabled={product?.isArchived}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <FormControlLabel
                control={<Switch checked={isScheduled} onChange={(e) => setIsScheduled(e.target.checked)} />}
                label={t('productDetails.isScheduled')}
                disabled={product?.isArchived}
              />
            </Grid>
            {categoryId === Commerce.ProductCategory.ALCOHOL && (
              <Grid item xs={12} md={4} lg={3}>
                <FormControlLabel
                  control={
                    <Switch checked={hasLimitedSaleTime} onChange={(e) => setLimitedSaleTime(e.target.checked)} />
                  }
                  label={t('productDetails.hasLimitedSaleTime')}
                  disabled={product?.isArchived}
                />
              </Grid>
            )}
            {!isInDefaultMenu && (
              <Grid item xs={12}>
                <Alert severity="warning">{t('productDetails.defaultMenuInfo')}</Alert>
              </Grid>
            )}
            {isScheduled && !isActive && (
              <Grid item xs={12} md={3}>
                <Alert severity="warning">{t('productDetails.productSchedulingInfo')}</Alert>
              </Grid>
            )}
            {isScheduled && (
              <>
                <Grid item xs={12} md={3}>
                  <DateTimePicker
                    name="startDate"
                    control={control}
                    label={t('global.start')}
                    error={errors['startDate']}
                    isDirty={isDirty}
                    defaultValue={undefined}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DateTimePicker
                    name="endDate"
                    control={control}
                    label={t('global.end')}
                    error={errors['endDate']}
                    isDirty={isDirty}
                    defaultValue={undefined}
                  />
                </Grid>
              </>
            )}
            {hasLimitedSaleTime && (
              <>
                <Grid item xs={12} md={3}>
                  <TimePicker
                    name="availableForSaleFrom"
                    control={control}
                    label={t('productDetails.availableForSaleFromLabel')}
                    error={errors['availableForSaleFrom']}
                    isDirty={isDirty}
                    defaultValue={undefined}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TimePicker
                    name="availableForSaleUntil"
                    control={control}
                    label={t('productDetails.availableForSaleUntilLabel')}
                    error={errors['availableForSaleUntil']}
                    isDirty={isDirty}
                    defaultValue={undefined}
                  />
                </Grid>
              </>
            )}
          </FormSection>
          <Box width="100%" />
          <FormSection>
            <Grid item xs={12}>
              <Typography variant="h2">{t('productDetails.productInformation')}</Typography>
              <CustomTextFieldInput
                name="name"
                label={t('productDetails.productNameLabel')}
                control={control}
                isDirty={isDirty}
                error={errors['name']}
                textFieldProps={{
                  inputProps: AutocompleteOff,
                  rows: 1,
                  multiline: true,
                  margin: 'normal',
                }}
              />
              <TextField
                label={t('productDetails.productSlugLabel')}
                value={productUrl}
                margin="normal"
                size="small"
                inputProps={{
                  readOnly: true,
                }}
              />
              <CustomTextFieldInput
                name="description"
                label={t('productDetails.productDescriptionLabel')}
                control={control}
                isDirty={isDirty}
                error={errors['description']}
                textFieldProps={{
                  inputProps: AutocompleteOff,
                  rows: 7,
                  multiline: true,
                  margin: 'normal',
                }}
              />
              <CustomTextFieldInput
                name="lunchDescription"
                label={t('productDetails.productLunchDescriptionLabel')}
                control={control}
                isDirty={isDirty}
                error={errors['lunchDescription']}
                textFieldProps={{
                  inputProps: AutocompleteOff,
                  rows: 7,
                  multiline: true,
                  margin: 'normal',
                }}
              />
              <small>{t('productDetails.productLunchDescriptionHint')}</small>
              <CustomTextFieldInput
                name="menuCardDescription"
                label={t('productDetails.productMenuCardDescriptionLabel')}
                control={control}
                isDirty={isDirty}
                error={errors['menuCardDescription']}
                textFieldProps={{
                  inputProps: AutocompleteOff,
                  rows: 4,
                  multiline: true,
                  margin: 'normal',
                }}
              />
              <small>{t('productDetails.productMenuCardDescriptionHint')}</small>
              <CustomTextFieldInput
                name="campaignDescription"
                label={t('productDetails.productCampaignDescriptionLabel')}
                control={control}
                isDirty={isDirty}
                error={errors['campaignDescription']}
                textFieldProps={{
                  inputProps: AutocompleteOff,
                  rows: 4,
                  multiline: true,
                  margin: 'normal',
                }}
              />
            </Grid>
            {selectedProductSizeId && (
              <>
                <Grid container item justifyContent="space-between" alignItems="center" direction="row">
                  <Typography variant="h2">{t('products.upcoming.productPictures')}</Typography>
                  {productSizesWithPrice.length > 1 &&
                    [Commerce.ProductCategory.PIZZA, Commerce.ProductCategory.MONSTER].includes(categoryId) && (
                      <FormControl component="fieldset">
                        <FormLabel>
                          <b>{t('Images for size')}:</b>
                        </FormLabel>
                        <RadioGroup
                          row
                          value={selectedProductSizeId}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setSelectedProductSizeId(+event.target.value);
                          }}
                        >
                          <>
                            {productSizesWithPrice.map((model) => {
                              const containsError = errors.productImagesFormData?.[model.sizeId];
                              return (
                                <FormControlLabel
                                  key={'radio' + model.sizeId}
                                  value={model.sizeId}
                                  control={<Radio />}
                                  label={
                                    <Box component="span" sx={containsError ? { color: 'error.main' } : undefined}>
                                      {model.sizeName}
                                    </Box>
                                  }
                                />
                              );
                            })}
                          </>
                        </RadioGroup>
                      </FormControl>
                    )}
                </Grid>
                <ImageContainer sx={{ backgroundColor }}>
                  <img src={imgPath} alt="Product picture" />
                </ImageContainer>
                <Grid padding={0} item xs={12} md={8}>
                  <CustomTextFieldInput
                    key={`productImagesFormData.${selectedProductSizeId}.imageUrl`}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    name={`productImagesFormData.${selectedProductSizeId}.imageUrl` as any}
                    label={t('productDetails.imagepathLabel')}
                    control={control}
                    isDirty={isDirty}
                    error={errors.productImagesFormData?.[selectedProductSizeId]?.imageUrl}
                    textFieldProps={{
                      onClick: () => {
                        const imageUrl = productImages?.[selectedProductSizeId]?.imageUrl || null;
                        if (imageUrl) {
                          setImagePath(imageUrl);
                        }
                      },
                      inputProps: AutocompleteOff,
                      rows: 1,
                      multiline: true,
                      margin: 'normal',
                    }}
                  />
                  <CustomTextFieldInput
                    key={`productImagesFormData.${selectedProductSizeId}.mobileImageUrl`}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    name={`productImagesFormData.${selectedProductSizeId}.mobileImageUrl` as any}
                    label={t('productDetails.imagepathMobileLabel')}
                    control={control}
                    isDirty={isDirty}
                    error={errors.productImagesFormData?.[selectedProductSizeId]?.mobileImageUrl}
                    textFieldProps={{
                      onClick: () => {
                        const mobileImageUrl = productImages?.[selectedProductSizeId]?.mobileImageUrl || null;
                        if (mobileImageUrl) {
                          setImagePath(mobileImageUrl);
                        }
                      },
                      inputProps: AutocompleteOff,
                      rows: 1,
                      multiline: true,
                      margin: 'normal',
                    }}
                  />
                  <CustomTextFieldInput
                    key={`productImagesFormData.${selectedProductSizeId}.menuImageUrl`}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    name={`productImagesFormData.${selectedProductSizeId}.menuImageUrl` as any}
                    label={t('productDetails.menuImagepathLabel')}
                    control={control}
                    isDirty={isDirty}
                    error={errors.productImagesFormData?.[selectedProductSizeId]?.menuImageUrl}
                    textFieldProps={{
                      onClick: () => {
                        const menuImageUrl = productImages?.[selectedProductSizeId]?.menuImageUrl || null;
                        if (menuImageUrl) {
                          setImagePath(menuImageUrl);
                        }
                      },
                      inputProps: AutocompleteOff,
                      rows: 1,
                      multiline: true,
                      margin: 'normal',
                    }}
                  />
                  <CustomTextFieldInput
                    key={`productImagesFormData.${selectedProductSizeId}.mobileMenuImageUrl`}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    name={`productImagesFormData.${selectedProductSizeId}.mobileMenuImageUrl` as any}
                    label={t('productDetails.menuImagepathMobileLabel')}
                    control={control}
                    isDirty={isDirty}
                    error={errors.productImagesFormData?.[selectedProductSizeId]?.mobileMenuImageUrl}
                    textFieldProps={{
                      onClick: () => {
                        const mobileMenuImageUrl = productImages?.[selectedProductSizeId]?.mobileMenuImageUrl || null;
                        if (mobileMenuImageUrl) {
                          setImagePath(mobileMenuImageUrl);
                        }
                      },
                      inputProps: AutocompleteOff,
                      rows: 1,
                      multiline: true,
                      margin: 'normal',
                    }}
                  />
                  <CustomTextFieldInput
                    key={`productImagesFormData.${selectedProductSizeId}.campaignImageUrl`}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    name={`productImagesFormData.${selectedProductSizeId}.campaignImageUrl` as any}
                    label={t('productDetails.campaignImageLabel')}
                    control={control}
                    isDirty={isDirty}
                    error={errors.productImagesFormData?.[selectedProductSizeId]?.campaignImageUrl}
                    textFieldProps={{
                      onClick: () => {
                        const campaignImageUrl = productImages?.[selectedProductSizeId]?.campaignImageUrl || null;
                        if (campaignImageUrl) {
                          setImagePath(campaignImageUrl);
                        }
                      },
                      inputProps: AutocompleteOff,
                      rows: 1,
                      multiline: true,
                      margin: 'normal',
                    }}
                  />
                  {product && backgroundColorProductCategories.includes(product.productCategoryId) && (
                    <MuiColorInput
                      margin="normal"
                      label={t('productDetails.backgroundColor')}
                      format="hex"
                      key={`productImagesFormData.${selectedProductSizeId}.backgroundColor`}
                      value={productImages?.[selectedProductSizeId]?.backgroundColor || ''}
                      onChange={(value) => {
                        if (matchIsValidColor(value)) {
                          setBackgroundColor(value);
                        } else {
                          setBackgroundColor('');
                        }
                      }}
                      isAlphaHidden
                    />
                  )}
                </Grid>
              </>
            )}
          </FormSection>
          <Grid item xs={12}>
            <Typography variant="h2">{t('Pricing model')}</Typography>
            <Box width="100%" paddingTop="1.5em" />
            <Paper>
              <RadioGroup row name="priceModel">
                <Grid item xs={12} lg={6} md={6} padding="1.5em">
                  <FormRadio
                    name="priceModel"
                    value={ProductPriceModel.FIXED_MIN_PRICE}
                    control={control}
                    label={<Typography variant="h3">2. {t('Locked minimum price')}</Typography>}
                  />

                  <ul>
                    <Typography variant="body2">
                      <li>{t("Minimum price isn't related to amount of ingredients")}.</li>
                      <li>{t("Removing ingredients doesn't decrease minimum price")}.</li>
                      <li>{t('E.g. seasonal products')}</li>
                    </Typography>
                  </ul>
                </Grid>

                <Grid item xs={12} lg={6} md={6} padding="1.5em">
                  <FormRadio
                    name="priceModel"
                    value={ProductPriceModel.MONSTER}
                    control={control}
                    label={<Typography variant="h3">3. {t('Monster model')}</Typography>}
                  />

                  <ul>
                    <Typography variant="body2">
                      <li>{t('Product has minimum price which includes ingredients for euro amount')}.</li>
                      <li>{t("Removing ingredients doesn't decrease minimum price")}.</li>
                      <li>{t('E.g. Monsters')}</li>
                    </Typography>
                  </ul>
                </Grid>
              </RadioGroup>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h2">{t('Size')}</Typography>
          </Grid>
          {categorySizes?.map((size) => (
            <Grid key={size.productSizeId} item xs={12} md={4} lg={3}>
              <ProductSizeCard
                title={String(size.name)}
                sizeId={size.productSizeId}
                onChange={handleSizeToggle}
                isLoading={isFetchingCategorySizes || isFetchingProductSizes || isFetchingPrices}
                selected={selectedSizeIds.includes(size.productSizeId)}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography variant="h2">{t('global.price')}</Typography>
          </Grid>
          <FormSection>
            <Grid container item>
              <Grid item xs={12} md={4} lg={3}>
                <Typography variant="h3" gutterBottom>
                  {t('Vat rate')}
                </Typography>
                <CustomSelect
                  name="vatId"
                  control={control}
                  label={t('vat')}
                  showEmptyOption={false}
                  isLoading={isInitialLoadingProductData}
                  options={vatRatesOptions}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <Typography variant="h3">{t('productDetails.discountsTitle')}</Typography>
                <div>
                  <small>{t('productDetails.isNonDiscountableHintText')}</small>
                </div>
                <FormCheckbox
                  name="isNonDiscountable"
                  control={control}
                  label={t('productDetails.isNonDiscountableLabel')}
                />
              </Grid>
            </Grid>
          </FormSection>
          <Grid container item gap={2}>
            {selectedSizeIds.map((selectedSizeId) => {
              const model = productSizesWithPrice.find((item) => item.sizeId === selectedSizeId);
              const priceModel = watch('priceModel') || ProductPriceModel.FIXED_MIN_PRICE;
              if (!model) return null;
              return (
                <Grid key={model.sizeId} item xs={12} md={4} lg={3}>
                  <ProductPriceCard
                    productSizeId={model.sizeId}
                    handleGroupChange={handlePriceGroupPriceChange}
                    priceGroups={productPriceGroups.filter((pg) => pg.productSizeId === model.sizeId)}
                    calculatedPrice={model.calculatedPrice || 0}
                    price={model.minimumPrice || model.calculatedPrice}
                    title={model.sizeName}
                    priceModel={priceModel}
                    handleChange={handlePriceChange}
                    isLoading={isFetchingPrices || isFetchingProductPriceGroups}
                  />
                </Grid>
              );
            })}
          </Grid>
          <Grid container item justifyContent="space-between" alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="h2">
                {t('products.upcoming.recipe')} {t('global.and')} {t('nav.ingredientDosings').toLowerCase()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProductIngredientSearch onIngredientSelect={handleAddIngredient} />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <ProductRecipeGrid
              productCategoryId={categoryId}
              productIngredientsRows={productIngredientsRows || []}
              comparison={s4dComparison}
              isRecipeLoaded={isRecipeLoaded}
              onIngredientQuantityChange={handleIngredientQuantityChange}
              onMaxQuantityChange={handleMaxQuantityChange}
              onIngredientRequiredChange={handleIngredientRequiredChange}
            />
          </Grid>
          <FormSection>
            {allIngredients && allIngredients.length > 0 && (
              <Grid container item justifyContent="space-between" alignItems="center" xs={12}>
                <Grid item xs={12} my={2}>
                  <Typography variant="h2">{t('Recommended ingredients')}</Typography>
                </Grid>
                <TagSelect
                  options={chefsRecommendationIngredientOptions}
                  onChange={(values) => {
                    const selected = allIngredients.filter((ingredient) =>
                      values.includes(String(ingredient.ingredientId))
                    );
                    setProductIngredientRecommendations([...selected]);
                  }}
                  value={productIngredientRecommendations.map((ingredient) => ({
                    label: ingredient.name,
                    value: String(ingredient.ingredientId),
                  }))}
                  id="product-recommended-ingredients-select"
                />
              </Grid>
            )}
            {tags && tags.length > 0 && (
              <Grid container item justifyContent="space-between" alignItems="center" xs={12} md={6}>
                <Grid item xs={12} my={2}>
                  <Typography variant="h2">{t('productDetails.productTags')}</Typography>
                </Grid>
                <TagSelect
                  options={tags.map((tag) => ({ label: tag.name, value: String(tag.id) })).sort(sortByLabel)}
                  onChange={(values) => {
                    setProductTags(tags.filter((tag) => values.includes(String(tag.id))));
                  }}
                  value={productTags.map((tag) => ({ label: tag.name, value: String(tag.id) }))}
                  id="product-tags-select"
                />
              </Grid>
            )}
            {displayGroups && displayGroups.length > 0 && (
              <Grid container item justifyContent="space-between" alignItems="center" xs={12} md={6}>
                <Grid item xs={12} my={2}>
                  <Typography variant="h2">{t('productDetails.displayGroups')}</Typography>
                </Grid>
                <TagSelect
                  options={displayGroups.map((displayGroup) => ({
                    label: displayGroup.name,
                    value: String(displayGroup.id),
                  }))}
                  onChange={(values) => {
                    const selected = displayGroups.filter((displayGroup) => values.includes(String(displayGroup.id)));
                    setProductDisplayGroups([...selected]);
                  }}
                  value={productDisplayGroups.map((displayGroup) => ({
                    label: displayGroup.name,
                    value: String(displayGroup.id),
                  }))}
                  id="product-display-groups-select"
                />
              </Grid>
            )}
            {showAlternativeProductSuggestion && (
              <Grid container item justifyContent="space-between" alignItems="center">
                <Grid item xs={12} my={2}>
                  <Typography variant="h2">{t('productDetails.alternativeProductSuggestion')}</Typography>
                </Grid>
                <Grid item xs={12} md={5} my={2}>
                  <SearchInput
                    onSelect={(value) => setValue('alternativeProductSuggestion.productId', Number(value))}
                    values={alternativeProductSuggestionValues}
                    label={
                      selectedAlternativeProductSuggestion
                        ? selectedAlternativeProductSuggestion
                        : t('productDetails.alternativeProduct')
                    }
                    noLabelPrefix={selectedAlternativeProductSuggestion ? true : false}
                  />
                </Grid>
                <Grid item xs={12} my={2}>
                  <CustomTextFieldInput
                    name="alternativeProductSuggestion.linkText"
                    label={t('productDetails.alternativeProductSuggestion')}
                    control={control}
                    isDirty={isDirty}
                    error={errors['alternativeProductSuggestion']}
                    textFieldProps={{
                      inputProps: AutocompleteOff,
                      rows: 2,
                      multiline: true,
                      margin: 'normal',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    onClick={() => {
                      setValue('alternativeProductSuggestion.linkText', undefined);
                      setValue('alternativeProductSuggestion.productId', 0);
                      setValue('alternativeProductSuggestion', null);
                    }}
                    startIcon={<Delete />}
                    disabled={upsertProductMutation.isLoading}
                    color="error"
                  >
                    {t('global.delete')} {t('productDetails.alternativeProductSuggestion')}
                  </Button>
                </Grid>
              </Grid>
            )}
            {showProductRecommendation && (
              <Grid container item justifyContent="space-between" alignItems="center">
                <Grid item xs={12} my={2}>
                  <Typography variant="h2">{t('productDetails.productRecommendations')}</Typography>
                </Grid>
                <Grid item xs={12} md={5} my={2}>
                  <SearchInput
                    onSelect={(value) => setValue('productRecommendation.recommendedProductId', Number(value))}
                    values={productRecommendationValues}
                    label={
                      selectedProductRecommendation
                        ? selectedProductRecommendation
                        : t('productDetails.recommendedProduct')
                    }
                    noLabelPrefix={selectedProductRecommendation ? true : false}
                  />
                </Grid>
                <Grid item xs={12} my={2}>
                  <CustomTextFieldInput
                    name="productRecommendation.recommendationText"
                    label={t('productDetails.recommendationText')}
                    control={control}
                    isDirty={isDirty}
                    error={errors['productRecommendation']}
                    textFieldProps={{
                      inputProps: AutocompleteOff,
                      rows: 2,
                      multiline: true,
                      margin: 'normal',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    onClick={() => {
                      setValue('productRecommendation.recommendationText', undefined);
                      setValue('productRecommendation.recommendedProductId', undefined);
                      setValue('productRecommendation', null);
                    }}
                    startIcon={<Delete />}
                    disabled={upsertProductMutation.isLoading}
                    color="error"
                  >
                    {t('global.delete')} {t('productDetails.recommendedProduct')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </FormSection>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <Typography variant="h2">S4D {t('Links').toLowerCase()}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ProductLinks
                  productId={productId}
                  productCategoryId={product?.productCategoryId || 0}
                  productSizePrices={productSizesWithPrice}
                  recipeIngredientIds={productIngredientsRows.map((row) => row.ingredientId)}
                  comparison={isRecipeLoaded ? s4dComparison : undefined}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Box>
      <Grid item xs={12} mt={6} container justifyContent="space-between">
        <SaveButton />
      </Grid>
      <BottomBar />
    </Box>
  );
};

export const sortByLabel = <T extends { label: string }>(a: T, b: T): number => {
  const nameA = a.label.toUpperCase();
  const nameB = b.label.toUpperCase();
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};

export const BottomBar = styled.div`
  height: 100px;
  width: 100%;
`;

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 16px 0;
`;

const FormSection: React.FC<PropsWithChildren> = ({ children }) => (
  <Grid item xs={12}>
    <StyledPaper>
      <Grid container spacing={3}>
        {children}
      </Grid>
    </StyledPaper>
  </Grid>
);

const ImageContainer = styled(Grid)`
  height: 396px;
  width: 396px;
  margin: 20px;
  border: solid darkgray 1px;
  border-radius: 4px;

  img {
    max-width: 392px;
    max-height: 392px;
  }
`;
